import { useEffect, useMemo, useState } from "react";
import { Button } from "V2.0/common/button";
import { putTimezoneSetting } from "../../service";
import { message } from "antd";
import { TimeSlots } from "./TimeSlots";
import { Heading } from "V2.0/common/heading";
import TimezoneSelect from "./TimeZoneSelectV2";
import ApplyOfficeHoursToQueue from "../CommunicationQueue/ApplyOfficeHoursToQueue";

const scheduleDataDefault = {
  monday: { checked: true, slots: [{ startTime: "10:00", endTime: "16:00", slotTag: "" }] },
  tuesday: { checked: true, slots: [{ startTime: "10:00", endTime: "16:00", slotTag: "" }] },
  wednesday: { checked: true, slots: [{ startTime: "10:00", endTime: "16:00", slotTag: "" }] },
  thursday: { checked: true, slots: [{ startTime: "10:00", endTime: "16:00", slotTag: "" }] },
  friday: { checked: true, slots: [{ startTime: "10:00", endTime: "16:00", slotTag: "" }] },
  saturday: { checked: true, slots: [{ startTime: "10:00", endTime: "16:00", slotTag: "" }] },
  sunday: { checked: true, slots: [{ startTime: "10:00", endTime: "16:00", slotTag: "" }] },
};

function TimeZoneSettings(props) {
  const {
    timezoneSettings,
    syncAssistant,
    workspaceId,
    assistantId,
    showSlotTags,
    locked,
    communicationQueueSettings,
  } = props;

  const [timezone, setTimezone] = useState("");
  const [daySchedule, setDaySchedule] = useState(scheduleDataDefault);
  const [saving, setSaving] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState(null);

  useEffect(() => {
    if (!timezoneSettings) return;
    const { timezone: tz, scheduleData } = timezoneSettings;
    const { _id, ...daysOfTheWeek } = scheduleData || {};
    setTimezone(tz || "");
    setDaySchedule(daysOfTheWeek);
    setSavedSnapshot(JSON.stringify({ tz, daysOfTheWeek }));
  }, [timezoneSettings]);

  // Drives the "Unsaved changes" label and keeps Save disabled until
  // there's actually something new to persist — one less thing to guess at.
  const isDirty = useMemo(() => {
    if (savedSnapshot === null) return false;
    return JSON.stringify({ tz: timezone, daysOfTheWeek: daySchedule }) !== savedSnapshot;
  }, [timezone, daySchedule, savedSnapshot]);

  const handleTimezoneChange = (event) => {
    setTimezone(event.target.value);
  };

  const handleSave = async () => {
    setSaving(true);
    message.loading("Updating office hours");
    const payload = { scheduleData: daySchedule, timezone };
    try {
      const response = await putTimezoneSetting({ workspaceId, assistantId, payload });
      if (response.status === 200) {
        message.destroy();
        message.success("Office hours updated");
        setSavedSnapshot(JSON.stringify({ tz: timezone, daysOfTheWeek: daySchedule }));
        syncAssistant((prevAssistant) => ({
          ...prevAssistant,
          timezoneSettings: response.data,
        }));
      }
    } catch (e) {
      message.destroy();
      message.error(
        e.response?.data?.msg ||
          e.response?.data?.message ||
          e.response?.data?.error ||
          "Failed to update office hours"
      );
      console.error("Error updating timezone settings", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="w-3/4 min-w-[700px] mx-4 mt-4 py-2">
        <ApplyOfficeHoursToQueue
          assistantId={assistantId}
          workspaceId={workspaceId}
          syncAssistant={syncAssistant}
          communicationQueueSettings={communicationQueueSettings}
          locked={locked}
        />
      </div>
      <div className="w-3/4 min-w-[700px] flex flex-col border rounded-lg m-4">
        <div className="flex items-center justify-between gap-4 px-6 py-4 bg-gray-50 rounded-t-lg border-b">
          <Heading className="text-md font-semibold text-gray-900">Office Hours</Heading>
          <div className="w-[240px]">
            <TimezoneSelect timezone={timezone} onTimezoneChange={handleTimezoneChange} locked={locked} />
          </div>
        </div>

        <TimeSlots
          data={daySchedule}
          setData={setDaySchedule}
          showSlotTags={showSlotTags}
          locked={locked}
        />

        {!locked && (
          <div className="flex items-center justify-between px-6 py-3 bg-gray-50 rounded-b-lg border-t">
            <span className="text-xs text-gray-400">
              {isDirty ? "Unsaved changes" : "All changes saved"}
            </span>
            <Button onClick={handleSave} disabled={!isDirty || saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export { TimeZoneSettings };